import type { JSONSchema } from 'json-schema-typed';
import type SB from '../SchemaBuilder';
import { unreachable } from '../utils/Utils';
import { mapObject } from '../utils/ObjUtils';

export function schemaBuilderToJSONSchema(schema: SB.Schema): Exclude<JSONSchema, boolean> {
    let defaultValue: unknown;

    try {
        defaultValue = schema.getDefault(); // Set `default` in JSON Schema if schema property has a valid default value
    } catch {
        // Ignore error
    }

    const base: Partial<JSONSchema> = {
        default: defaultValue,
    };

    switch (schema._type) {
        case 'null':
            return { ...base, type: 'null' };

        case 'boolean':
            return { ...base, type: 'boolean' };

        case 'number': {
            const { minVal, maxVal } = (schema as SB.NumberSchema).serialize();

            return {
                ...base,
                type: 'number',
                ...(Number.isSafeInteger(minVal) ? { minimum: minVal } : {}),
                ...(Number.isSafeInteger(maxVal) ? { maximum: maxVal } : {}),
            };
        }

        case 'integer': {
            const { minVal, maxVal } = (schema as SB.IntegerSchema).serialize();

            return {
                ...base,
                type: 'integer',
                ...(Number.isSafeInteger(minVal) ? { minimum: minVal } : {}),
                ...(Number.isSafeInteger(maxVal) ? { maximum: maxVal } : {}),
            };
        }

        case 'string':
            return { ...base, type: schema._type };

        case 'tuple': {
            const { values } = (schema as SB.TupleSchema<any>).serialize();

            return {
                ...base,
                type: 'string',
                enum: values,
            };
        }

        case 'array': {
            const arraySchema = schema as SB.ArraySchema<any>;
            const itemSchema = arraySchema.getItemSchema();
            const { minLengthVal, maxLengthVal } = arraySchema.serialize();

            return {
                ...base,
                type: 'array',
                items: schemaBuilderToJSONSchema(itemSchema),
                ...(Number.isSafeInteger(minLengthVal) ? { minItems: minLengthVal } : {}),
                ...(Number.isSafeInteger(maxLengthVal) ? { maxItems: maxLengthVal } : {}),
            };
        }

        case 'object': {
            const objectSchema = schema as SB.ObjectSchema<Record<string, SB.Schema>>;
            const additionalPropertyValuesSchema = objectSchema.getAdditionalPropertyValuesSchema();
            const propertySchemas = objectSchema.getFullSchema();

            return {
                ...base,
                type: 'object',
                additionalProperties: additionalPropertyValuesSchema
                    ? schemaBuilderToJSONSchema(additionalPropertyValuesSchema)
                    : false,
                properties: mapObject(propertySchemas, (_, propertySchema) =>
                    schemaBuilderToJSONSchema(propertySchema),
                ),
                required: Object.entries(propertySchemas)
                    .filter(([_, propertySchema]) => propertySchema.required)
                    .map(([propertyName, _]) => propertyName),
            };
        }

        case 'map': {
            const mapSchema = schema as SB.MapSchema<Record<string, SB.Schema>>;
            const itemSchema = mapSchema.getItemSchema();

            return {
                ...base,
                type: 'object',
                additionalProperties: schemaBuilderToJSONSchema(itemSchema),
            };
        }

        case 'union': {
            const unionSchema = schema as SB.UnionSchema<any>;
            const itemSchemas = unionSchema.getItemSchemas();

            return {
                ...base,
                oneOf: itemSchemas.map(schemaBuilderToJSONSchema),
            };
        }

        case 'unknown': {
            return { ...base };
        }

        default:
            unreachable(schema._type);
    }
}
